"use client";

import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import Link from "next/link";
import Image from "next/image";
import { Heart, ShoppingBasketIcon, Menu, X } from "lucide-react";
import UserAccountDropdown from "./UserAccountDropDown";
import { CartBadge } from "@/components/ui/icons/CartIcon";
import { WishlistBadge } from "@/components/ui/icons/WishlistIcon";

const NavLinks = [
    {
        title: "Home",
        link: "/"
    },
    {
        title: "About us",
        link: "/about-us"
    },
    {
        title: "Our Services",
        link: "/our-services"
    },
    {
        title: "Shop",
        link: "/shop"
    },
    {
        title: "Our Blog",
        link: "/blog"
    },
    {
        title: "Contact us",
        link: "/contact-us"
    }
];

export default function Navbar() {

    const [isScrolled, setScrolled] = useState(false);
    const [authOpen, setAuthOpen] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const pathname = usePathname();
    
    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > -1);
        };
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    useEffect(() => {
        if (mobileMenuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [mobileMenuOpen]);

    return (
        <div>

            {/* Desktop navbar */}
            <div className="w-full fixed top-0 left-0 z-50 flex items-center px-4 md:px-15 3xl:px-50 py-0.5 md:py-0 shadow-md bg-white">
                <div className="flex w-full items-center justify-between">
                    
                    {/* Logo */}
                    <div className="flex relative w-48 md:w-72 3xl:w-80 h-14 md:h-18 z-50">
                        <Link href="/" className="relative w-full h-full">
                            <Image
                                src="/images/mobetts-nakuru.png"
                                alt="mobetts-agro-centre-logo"
                                fill
                                className="object-contain object-left"
                                priority
                            />
                        </Link>
                    </div>

                    {/* Desktop Nav */}
                    <nav className="hidden lg:flex flex-1 justify-center">
                        <ul className="flex items-center gap-6 md:gap-12 3xl:gap-14">
                            {NavLinks.map((nav, index) => (
                                <li key={index}>
                                    <Link href={nav.link} className={`text-sm md:text-sm uppercase 3xl:text-sm ${pathname === nav.link ? "text-burntUmber" : "hover:text-burntUmber"} transition-colors whitespace-nowrap`}>
                                        {nav.title}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </nav>

                    {/* Right Icons */}
                    <div className="flex items-center gap-0 md:gap-8 3xl:gap-10 z-50">
                        {/* <CartBadge /> */}
                        
                        {/* Wishlist Badge - Hidden on mobile, visible on sm and up */}
                        {/* <div className="hidden sm:block">
                            <WishlistBadge />
                        </div> */}

                        <div className="sm:block pl-5">
                            <UserAccountDropdown onLoginClick={() => setAuthOpen(true)} />
                        </div>

                        {/* Mobile Menu Button */}
                        <button 
                            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                            className="lg:hidden pl-3"
                            aria-label="Toggle menu"
                        >
                            {mobileMenuOpen ? (
                                <X className="w-4 h-4" />
                            ) : (
                                <Menu className="w-6 h-6" />
                            )}
                        </button>
                    </div>

                </div>
            </div>

            {/* Mobile Menu Overlay */}
            {mobileMenuOpen && (
                <div 
                    className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
                    onClick={() => setMobileMenuOpen(false)}
                />
            )}

            {/* Mobile Menu */}
            <div className={`fixed top-0 md:top-26 right-0 h-[calc(100vh-5rem)] md:h-[calc(100vh-6.5rem)] w-full sm:w-80 bg-white z-40 transform transition-transform duration-300 ease-in-out lg:hidden ${
                mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
            }`}>
                <nav className="flex flex-col h-full">
                    <ul className="flex flex-col p-6 space-y-4 flex-1">
                        {NavLinks.map((nav, index) => (
                            <li key={index}>
                                <Link 
                                    href={nav.link} 
                                    className="uppercase text-lg font-medium hover:text-yellow-500 transition-colors block py-2"
                                    onClick={() => setMobileMenuOpen(false)}
                                >
                                    {nav.title}
                                </Link>
                            </li>
                        ))}
                    </ul>

                    {/* Mobile Menu Footer */}
                    <div className="border-t p-6 space-y-4">
                        {/* <Link 
                            href="/wishlist" 
                            className="flex items-center gap-3 text-lg hover:text-yellow-500 transition-colors sm:hidden"
                            onClick={() => setMobileMenuOpen(false)}
                        >
                            <Heart className="w-5 h-5" />
                            <span>Wishlist</span>
                        </Link> */}
                        
                        <div className="sm:hidden">
                            <UserAccountDropdown onLoginClick={() => {
                                setAuthOpen(true);
                                setMobileMenuOpen(false);
                            }} />
                        </div>
                    </div>
                </nav>
            </div>

        </div>
    );
}